import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  StringReplacementInstruction,
  transformSvg,
} from '@epicuro-next/ataraxia/svg';
import { PropertyPathSvgReplacement } from '@epicuro-next/constants/rule-engine/pulse-rules';
import { getPropertyValueByPath, isString } from '@epicuro-next/utilities';
import { Product } from 'graphql-api/gql-api-interfaces';

type ProductStringReplacementInstruction = (
  input: Product,
) => StringReplacementInstruction;
@Component({
  selector: 'atx-product-image-missing-visual',
  templateUrl: './product-image-missing-visual.component.html',
  styleUrls: ['./product-image-missing-visual.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductImageMissingVisualComponent {
  private static readonly mainColorPlaceholder = 'MAIN_COLOR_3879ab-ef-038';
  public static readonly defaultMainColor = '#CCCCCC';
  private readonly defaultSvg = `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <circle cx="50" cy="50" r="16.66" fill="${ProductImageMissingVisualComponent.mainColorPlaceholder}" stroke="#D7D7D7" stoke-width="1" />
</svg>`;
  private readonly defaultReplacementInstructions: PropertyPathSvgReplacement[] =
    [
      {
        findInSvg: ProductImageMissingVisualComponent.mainColorPlaceholder,
        propertyPath: 'attributes.hexcode',
      },
    ];

  @Input() public product: Product;
  @Input() public svg?: string = this.defaultSvg;
  @Input() public replacementInstructions? = [
    ...this.defaultReplacementInstructions,
  ];

  protected get getReplacementInstructions(): ProductStringReplacementInstruction[] {
    const replacementInstructions =
      this.replacementInstructions ?? this.defaultReplacementInstructions;

    const instructions: ProductStringReplacementInstruction[] = [];

    replacementInstructions.forEach((instruction) => {
      const replacement = this.getNestedProperty(
        this.product,
        instruction.propertyPath,
      );

      if (replacement !== undefined) {
        const method: ProductStringReplacementInstruction = () => {
          return {
            find: instruction.findInSvg,
            replacement,
          };
        };

        instructions.push(method);
      }
    });

    return instructions;
  }

  protected get svgContent(): string {
    let transformedSvg = transformSvg(
      this.svg ?? this.defaultSvg,
      this.product,
      this.getReplacementInstructions,
    );

    if (
      transformedSvg.includes(
        ProductImageMissingVisualComponent.mainColorPlaceholder,
      )
    ) {
      transformedSvg = transformedSvg.replace(
        ProductImageMissingVisualComponent.mainColorPlaceholder,
        ProductImageMissingVisualComponent.defaultMainColor,
      );
    }

    return transformedSvg;
  }

  private getNestedProperty(
    p: Product,
    propertyString: string,
  ): string | undefined {
    const value = getPropertyValueByPath(p, propertyString);

    if (isString(value)) {
      return value;
    }

    return undefined;
  }
}
